<template>
  <main class="bg-black c-white" style="--theme: #00ff73">
    <!-- Hero section -->
    <div class="mx-a" style="max-width: 120rem">
      <base-picture v-bind="hero.background" class="fixed top-0 full lg:w-a" />
      <div class="relative mb-10">
        <base-picture
          v-style:h="{ sm: 'calc(100vh - 14.93vw)', md: 'auto' }"
          v-bind="hero.image"
          class="relative w-full"
          fit="cover"
        />
        <div class="absolute bottom-0 flex items-center md:absolute-0">
          <div v-style:p="{ sm: '5rem 2rem', md: '6rem 0 0 5rem', lg: '9rem 0 0 7.5rem' }" class="space-y-4">
            <h2
              v-style:text-size="{ sm: '14vw', md: 'min(5vw, 5.625rem)' }"
              v-html="hero.title"
              class="c-$theme fw-bold"
              style="line-height: .85"
            />
            <p class="w-4/5 fw-medium md:w-3/5 md:text-xs lg:text-base">
              {{ hero.description }}
            </p>
            <vf-button to="#how-it-works" variant="themable-inverse">
              {{ $t.learnMore }}
            </vf-button>
          </div>
        </div>
      </div>
      <!-- Main Content -->
      <div class="relative z-1 mx-a mt-10 space-y-15" style="max-width: 75rem">
        <!-- How it works -->
        <section id="how-it-works" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="mx-8 h-15 b-y b-white py-4 text-center c-$theme md:mx-5">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="relative inline bg-black px-4 py-2 text-xl fw-bold lg:text-4xl" style="top: -2rem">
              {{ howItWorks.heading }}
            </h2>
          </div>
          <p class="ml-5 mt-5 text-xs <md:text-center">
            {{ howItWorks.subheading }}
          </p>
          <vf-carousel class="mt-10 w-full <md:pl-8">
            <div
              v-for="({ body, heading, icon }, key) in howItWorks.slides"
              :key
              class="mb-15 w-4/5 f-col pr-4 md:w-3/8 md:flex-row"
            >
              <h2 v-style:text-size="{ sm: '15.5vw', md: '7rem', lg: '10rem' }" class="ml-2 w-1/4 flex items-end c-$theme fw-bold lh-none">
                {{ key + 1 }}
              </h2>
              <div class="ml-2 b-t-2 b-white md:w-4/5 space-y-4">
                <base-picture v-bind="icon" class="mt-4" />
                <h2 class="text-lg fw-bold lg:text-lg md:text-xs">
                  {{ heading }}
                </h2>
                <p v-style:text-size="{ sm: '.75rem', md: '.55rem', lg: '.875rem' }" class="lh-none">
                  <base-interpolator :content="body">
                    <template #link="{ args: [message] }">
                      <vf-link class="fw-bold" to="#pricing">
                        {{ message }}
                      </vf-link>
                    </template>
                  </base-interpolator>
                </p>
              </div>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="absolute bottom-0 right-3/4 lg:right-1/5 md:right-1/4"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="absolute bottom-0 right-1/4 md:right-0"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="absolute bottom-0 right-1/3 w-1/3 rounded-full bg-white md:right-0 md:mr-9 md:w-1/5 lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-$theme"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Store -->
        <section id="stores" class="mx-8 md:mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="h-15 b-y b-white pb-16 pt-4 text-center c-$theme md:py-4">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2
              v-html="stores.heading"
              class="relative inline bg-black px-4 py-2 text-xl fw-bold lg:text-4xl"
              style="top: -2rem"
            />
          </div>
          <vf-carousel class="mt-10 w-full <md:px-6">
            <div
              v-for="({ address, name, to }, key) in stores.slides.slice(0, stores.count)"
              :key
              class="mb-15 w-full b b-$theme bg-grey-10 px-10 py-6 between"
              :class="stores.count > 1 ? 'mr-8 md:w-2/5 md:f-col' : 'f-col md:flex-row'"
            >
              <div class="<md:space-y-4" :class="{ 'space-y-8 mb-8': stores.count > 1 }">
                <h2 v-html="name" class="fw-bold lg:text-lg md:text-sm" />
                <p class="flex items-center text-sm lg:text-sm md:text-xs">
                  <vf-icon class="c-$theme" name="location" size="md" />
                  <span v-html="address" />
                </p>
              </div>
              <vf-button class="w-a <md:mt-4" :to variant="themable-inverse">
                {{ stores.cta }}
              </vf-button>
            </div>
            <template #prev="{ active, action }">
              <base-button
                v-if="stores.count > 1"
                :aria-label="$t.previous"
                class="absolute bottom-0 right-3/4 lg:right-1/5 md:right-1/4"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                v-if="stores.count > 1"
                :aria-label="$t.next"
                class="absolute bottom-0 right-1/4 md:right-0"
                :class="{ '<md:right-1/6': stores.count > 1, 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-if="stores.count > 1"
                v-slot="{ percentage }"
                class="absolute bottom-0 right-1/3 w-1/3 rounded-full bg-white md:right-0 md:mr-9 md:w-1/5 lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-$theme"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Services -->
        <section id="services" class="mx-8 md:mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="h-15 b-y b-white pb-16 pt-4 text-center c-$theme md:py-4">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="relative inline bg-black px-4 py-2 text-xl fw-bold lg:text-4xl" style="top: -2rem">
              {{ services.heading }}
            </h2>
          </div>
          <vf-carousel class="mt-10 w-full <md:hidden">
            <div
              v-for="({ body, heading, icon }, key) in services.slides.flat()"
              :key
              class="mb-15 w-1/4 pr-8 space-y-4"
            >
              <base-picture v-bind="icon" class="mt-4" />
              <h2 class="text-sm fw-bold lg:text-lg">
                {{ heading }}
              </h2>
              <p class="text-xs lg:text-sm">
                {{ body }}
              </p>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="absolute bottom-0 right-1/4 lg:right-1/5"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="absolute bottom-0 right-0"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="absolute bottom-0 right-0 mr-9 w-1/5 rounded-full bg-white lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-$theme"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
          <vf-carousel class="mt-10 w-full md:hidden">
            <div v-for="(items, key) in services.slides" :key class="mb-10 w-full">
              <div
                v-for="({ body, heading, icon }, i) in items"
                :key="i"
                class="mx-10 space-y-4"
              >
                <base-picture v-bind="icon" class="mt-4" />
                <h2 class="text-lg fw-bold">
                  {{ heading }}
                </h2>
                <p class="text-sm">
                  {{ body }}
                </p>
              </div>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="absolute bottom-0 right-3/4 -mr-8"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="absolute bottom-0 right-1/4"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="absolute bottom-0 right-1/3 w-1/3 rounded-full bg-white"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-$theme"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Pricing -->
        <section id="pricing" class="md:mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="h-15 b-y b-white py-4 text-center c-$theme <md:mx-8">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="relative inline bg-black px-4 py-2 text-xl fw-bold lg:text-4xl" style="top: -2rem">
              {{ pricing.heading }}
            </h2>
          </div>
          <vf-carousel class="mt-10 w-full <md:pl-8">
            <div
              v-for="({ body, count, list, price, title }, key) in pricing.slides.slice(0, pricing.count)"
              :key
              class="mb-15 w-9/10"
              :class="pricing.count < 4 ? 'md:w-1/3' : 'md:w-2/5'"
            >
              <div class="mr-4 h-full f-col b b-$theme bg-grey-10 p-8 space-y-3">
                <h2 class="text-xl fw-bold">
                  {{ title }}
                </h2>
                <div class="flex">
                  <p class="text-6xl c-$theme fw-bold lh-none">
                    {{ price }}
                  </p>
                  <span class="flex self-end text-xs c-white">
                    &nbsp;{{ pricing.perItem }}
                  </span>
                </div>
                <p class="text-xs">
                  {{ body }}
                </p>
                <ul class="text-sm lg:text-base <md:space-y-3">
                  <li v-for="(item, i) in list.slice(0, count)" :key="i" class="flex fw-light">
                    <vf-icon class="mr-1 c-$theme" name="success" size="md" /> {{ item }}
                  </li>
                </ul>
              </div>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="absolute bottom-0 right-3/4 lg:right-1/5 md:right-1/4"
                :class="{ 'md:hidden': pricing.count < 4, 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="absolute bottom-0 right-1/4 md:right-0"
                :class="{ 'md:hidden': pricing.count < 4, 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="absolute bottom-0 right-1/3 w-1/3 rounded-full bg-white md:right-0 md:mr-9 md:w-1/5 lg:w-1/6"
                :class="{ 'md:hidden': pricing.count < 4 }"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-$theme"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
        </section>
        <!-- Tutorials -->
        <section id="tutorials" class="mx-5" :class="header.height.promoBar ? 'scroll-mt-40' : 'scroll-mt-15'">
          <div class="h-15 b-y b-white py-4 text-center c-$theme <md:mx-8">
            <base-picture v-bind="hero.divider" class="full" fit="none" />
            <h2 class="relative inline bg-black px-4 py-2 text-xl fw-bold lg:text-4xl" style="top: -2rem">
              Tutorials
            </h2>
          </div>
          <vf-carousel class="mt-10 w-full <md:hidden">
            <div
              v-for="({ body, heading, image }, key) in tutorials.slides"
              :key
              class="mb-20 w-1/3 pr-4 space-y-4"
            >
              <vf-link class="relative" variant="quieter" @click="tutorialsModal = true; activeIndex = key">
                <base-picture v-bind="tutorials.playIcon" class="absolute-0 m-a w-1/5" />
                <base-picture v-bind="image" />
              </vf-link>
              <p class="text-xs">
                {{ body }}
              </p>
              <h2 class="text-lg c-$theme fw-bold">
                {{ heading }}
              </h2>
            </div>
            <template #prev="{ active, action }">
              <base-button
                :aria-label="$t.previous"
                class="absolute bottom-0 right-1/4 lg:right-1/5"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon dir="left" name="chevron" size="lg" />
              </base-button>
            </template>
            <template #next="{ active, action }">
              <base-button
                :aria-label="$t.next"
                class="absolute bottom-0 right-0"
                :class="{ 'c-grey-20': !active }"
                :disabled="!active"
                @click="action"
              >
                <vf-icon name="chevron" size="lg" />
              </base-button>
            </template>
            <template #pagination="{ active, pages }">
              <base-progress
                v-slot="{ percentage }"
                class="absolute bottom-0 right-0 mr-9 w-1/5 rounded-full bg-white lg:w-1/6"
                :max="pages"
                role="presentation"
                style="height: .25rem; margin-bottom: .875rem"
                :value="active + 1"
              >
                <div
                  class="h-full rounded-full bg-$theme"
                  :style="{
                    'transform': `scaleX(${percentage / 100})`,
                    'transform-origin': 'left',
                    'transition': 'transform .6s cubic-bezier(.16, 1, .3,1)',
                  }"
                />
              </base-progress>
            </template>
          </vf-carousel>
          <vf-link
            v-for="({ body, heading }, key) in tutorials.slides"
            :key
            class="mx-15 my-10 flex md:hidden"
            variant="quieter"
            @click="tutorialsModal = true; activeIndex = key"
          >
            <div class="px-5">
              <p>
                {{ body }}
              </p>
              <h2 class="c-$theme fw-bold">
                {{ heading }}
              </h2>
            </div>
            <base-picture v-bind="tutorials.playIcon" class="w-1/6" />
          </vf-link>
        </section>
        <!-- Terms and conditions -->
        <section class="pb-20 text-center text-sm c-grey-40">
          <vf-link to="/repair-service/terms-and-conditions">
            {{ termsAndConditions }}
          </vf-link>
        </section>
      </div>
    </div>
    <transition name="nested">
      <vf-modal v-if="tutorialsModal" size="lg" style-content="background: none" @resolve="tutorialsModal = false">
        <vf-carousel ref="scroller" loop @init="scroller.scroll.scrollToIndex(activeIndex, false)">
          <div v-for="({ image, videoSrc }, key) in tutorials.slides" :key class="m-a w-full">
            <span class="ml-5 md:ml-10">{{ key + 1 }}/{{ tutorials.slides.length }}</span>
            <base-video class="m-a w-4/5" :poster="image.src" :src="videoSrc" />
          </div>
        </vf-carousel>
      </vf-modal>
    </transition>
    <!-- Footer nav -->
    <section class="sticky bottom-0 z-1 w-full flex overflow-x-auto bg-$theme py-4 md:center">
      <template v-for="({ id, name }, key) in footerNav" :key>
        <vf-link :to="`#${id}`" variant="quieter">
          <p
            class="w-max b-black px-8 c-black fw-bold uppercase md:text-xs"
            :class="{ 'b-r': key + 1 !== footerNav.length }"
          >
            {{ name }}
          </p>
        </vf-link>
      </template>
    </section>
  </main>
</template>

<script lang="ts" setup>
const { $locale, $t } = useNuxtApp()
const { supportedLocales = [] } = useAppConfig().pages.repairService

definePageMeta({ header: { transparent: true } })

if (!supportedLocales.includes($locale))
  throw createError({ message: `Page is not available for ${$locale} locale`, statusCode: 404, fatal: true })

const { data, error } = await useApi().internal.getStaticPage('repair-service')
if (!data.value) throw createError({ ...error.value!, fatal: true })

const { hero, howItWorks, stores, services, pricing, tutorials, termsAndConditions, footerNav } = data.value
const activeIndex = ref()
const scroller = ref()
const tutorialsModal = ref(false)

const header = useHeaderStore()
</script>
